import { MPLUS_DUNGEON_IDS } from '@keystone/content';
/**
 * Pure view-model of the Dungeons screen (mockup docs/art/mockups/dungeon.jpg; DOM-free, tested in
 * dungeonsModel.test.ts): one card per real dungeon (key, target, best, affixes, reward slots),
 * the Weekly tab rows (Daily Offering, Weekly Ritual, Vault) and the Rank tab (tier, bar, ladder).
 */
import { type AffixId, type ContentRegistry, affixesForLevel } from '@keystone/sim';
import {
  type Profile,
  type RankTier,
  RANK_TIERS,
  RATED_DUNGEONS,
  WEEKLY_RITUAL_RUNS,
  dailyStatus,
  dayNumber,
  isoWeek,
  nextRunHint,
  profileStanding,
} from '@keystone/progression';
import type { HubPrefs, LastVault } from '../hubPrefs';
import { type ResourceAmount, bundleEntries } from '../forge/resourceText';
import { clampKey, maxUnlockedKey, MIN_KEY } from '../run';
import { roman } from '../ui/dom';
import { nextUnlocks } from './rankModel';
import { vaultView } from './vaultModel';

/** Rank tier accent colours (crest labels, rank title). */
export const TIER_COLOR: Readonly<Record<RankTier, string>> = { novice: '#9aa4b5', contender: '#4fd18b', champion: '#4b9dff', paragon: '#b36bff', eternal: '#ff9a3d' };

export type DungeonsTab = 'mplus' | 'weekly' | 'rank';
export const DUNGEON_TABS: readonly DungeonsTab[] = ['mplus', 'weekly', 'rank'];

export function dungeonsTab(raw: unknown): DungeonsTab {
  return DUNGEON_TABS.includes(raw as DungeonsTab) ? (raw as DungeonsTab) : 'mplus';
}

/** "14:23" (m:ss). */
export function clock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export interface DungeonCard {
  id: string;
  name: string;
  /** Not rated (Training Grounds): no rating, no reward preview. */
  practice: boolean;
  level: number;
  min: number;
  max: number;
  target: string;
  /** "+6  14:23", or null before the first timed clear. */
  best: string | null;
  affixes: AffixId[];
  /** Up to 4 resources a timed run at this key pays (a hint). */
  rewards: ResourceAmount[];
}

export function dungeonCards(p: Profile, prefs: Pick<HubPrefs, 'level'>, content: Pick<ContentRegistry, 'bundle'>, rated: readonly string[] = RATED_DUNGEONS): DungeonCard[] {
  const level = clampKey(p, prefs.level);
  const max = maxUnlockedKey(p);
  let rewards: ResourceAmount[] = [];
  try {
    rewards = bundleEntries(nextRunHint(p, level).resources).slice(0, 4);
  } catch {
    rewards = [];
  }
  const cards = content.bundle.dungeons.filter((d) => MPLUS_DUNGEON_IDS.includes(d.id)).map((d): DungeonCard => {
    const practice = !rated.includes(d.id);
    const rec = p.rating[d.id]?.best ?? null;
    return {
      id: d.id,
      name: d.name,
      practice,
      level,
      min: MIN_KEY,
      max,
      target: clock(d.timerMs),
      best: rec ? `+${rec.level}  ${clock(rec.elapsedMs)}` : null,
      affixes: affixesForLevel(level),
      rewards: practice ? [] : rewards,
    };
  });
  // Rated dungeons first (the Venom Altar), practice last.
  return cards.sort((a, b) => Number(a.practice) - Number(b.practice));
}

export interface WeeklyRow {
  id: 'daily' | 'ritual' | 'vault';
  title: string;
  value: number;
  max: number;
  status: string;
  ready: boolean;
}

const LAST_VAULT: Readonly<Record<LastVault, string>> = { altar: 'Altar Cache', scales: 'Scale Cache', token: 'Vault Forge Token' };

export function weeklyRows(p: Profile, localDate: string): WeeklyRow[] {
  const daily = dailyStatus(p.forge, localDate);
  const w = p.forge.weekly;
  const runs = Math.min(WEEKLY_RITUAL_RUNS, w.isoWeek === isoWeek(dayNumber(localDate)) ? w.runs : 0);
  const v = vaultView(p);
  return [
    { id: 'daily', title: 'Daily Offering', value: daily.ready ? 0 : 1, max: 1, status: daily.ready ? 'Ready: finish any key' : 'Claimed today', ready: daily.ready },
    { id: 'ritual', title: 'Weekly Ritual', value: runs, max: WEEKLY_RITUAL_RUNS, status: runs >= WEEKLY_RITUAL_RUNS ? 'Claimed this week' : `${WEEKLY_RITUAL_RUNS - runs} more run${WEEKLY_RITUAL_RUNS - runs === 1 ? '' : 's'}`, ready: false },
    { id: 'vault', title: 'Vault', value: v.runsInWindow, max: v.every, status: `${v.runsLeft} more run${v.runsLeft === 1 ? '' : 's'}`, ready: v.ready },
  ];
}

export function lastVaultText(last: LastVault | null): string {
  return last ? `Last vault: ${LAST_VAULT[last]}` : '';
}

export interface RankView {
  tier: RankTier;
  label: string;
  rating: number;
  /** Progress through the current sub-rank (0..1). */
  fill: number;
  /** "312 to Paragon I", or the top-of-ladder line. */
  toNext: string;
  ladder: { tier: RankTier; name: string; state: 'done' | 'current' | 'locked' }[];
  /** One line: what the next tier unlocks. */
  nextUnlock: string;
  rows: { name: string; best: string }[];
}

export function rankView(p: Profile, content: Pick<ContentRegistry, 'bundle'>, rated: readonly string[] = RATED_DUNGEONS): RankView {
  const s = profileStanding(p);
  const idx = RANK_TIERS.findIndex((t) => t.tier === s.tier);
  const def = RANK_TIERS[idx]!;
  const name = (t: RankTier): string => RANK_TIERS.find((x) => x.tier === t)?.name ?? t;
  // Sub-rank floor: tier min, then evenly up to the next tier (or +100 per sub-rank at the top).
  const nextTier = RANK_TIERS[idx + 1] ?? null;
  const span = nextTier ? (nextTier.minRating - def.minRating) / def.subRanks : 100;
  const floor = def.minRating + span * (s.subRank - 1);
  const fill = s.nextAt === null ? 1 : Math.max(0, Math.min(1, (s.rating - floor) / Math.max(1, s.nextAt - floor)));
  const nextName = s.subRank < def.subRanks ? `${def.name} ${roman(s.subRank + 1)}` : nextTier ? `${nextTier.name} I` : null;
  const toNext = s.nextAt === null || !nextName ? 'Top of the ladder' : `${Math.max(0, Math.ceil(s.nextAt - s.rating)).toLocaleString('en-US')} to ${nextName}`;
  const nu = nextUnlocks(s);
  const perk = nu.withinTier[0]?.text ?? nu.perks[0]?.text ?? null;
  const nextUnlock = nu.withinTier[0] ? `${def.name} ${roman(nu.withinTier[0].atSubRank ?? 1)}: ${perk}` : nu.next && perk ? `${nu.next.name}: ${perk}` : 'Every unlock is yours';
  return {
    tier: s.tier,
    label: `${def.name} ${roman(s.subRank)}`,
    rating: Math.round(s.rating),
    fill,
    toNext,
    ladder: RANK_TIERS.map((t, i) => ({ tier: t.tier, name: name(t.tier), state: i < idx ? 'done' : i === idx ? 'current' : 'locked' })),
    nextUnlock,
    rows: content.bundle.dungeons
      .filter((d) => MPLUS_DUNGEON_IDS.includes(d.id))
      .filter((d) => rated.includes(d.id))
      .map((d) => {
        const b = p.rating[d.id]?.best;
        return { name: d.name, best: b ? `+${b.level}  ${clock(b.elapsedMs)}` : 'No timed run yet' };
      }),
  };
}
